import { urgencyRank } from "./hiveUrgency";

/**
 * Sites as the structural layer of the dashboard (#282, absorbing #266).
 *
 * WHY THIS EXISTS
 * ---------------
 * A list of fifty hives is unusable, and the reason is not its length --
 * it is that it is ordered by identity (site, then hive number) rather
 * than by need. Fifty hives ordered by urgency is fine, because only the
 * top is ever read.
 *
 * Sites are what make the rest of it scale. A site card COMPRESSES its
 * hives into one line instead of listing them, so twenty sites stays a
 * readable grid where twenty sites' worth of hives does not.
 *
 * Derived here from the hive list the dashboard already loads, rather
 * than from a new endpoint. needs_attention and next_check_due are
 * computed by the API from the account's inspection interval (V034), so
 * nothing in this file decides WHEN a hive is due -- only how a yard full
 * of them summarises. That is the same discipline hiveUrgency follows,
 * and for the same reason: two screens that decide independently which
 * hives are overdue will eventually disagree in front of a user.
 */

/** Due within this many days counts as "coming up" rather than "fine".
 *
 * Three days is a planning horizon, not a deadline: it is roughly the
 * notice someone needs to fit a yard into a week that already has other
 * things in it. */
export const SOON_DAYS = 3;

/** Worst first. The order here IS the site's colour and its sort. */
export const SITE_STATES = ["issues", "overdue", "soon", "current"];

export function daysUntil(dateish) {
  if (!dateish) return null;
  // A bare YYYY-MM-DD parses as UTC midnight and lands a day early in
  // Central time -- the bug fixed in 9e64d40. Compare date parts only.
  const due = new Date(`${String(dateish).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
}

/**
 * One site's summary.
 *
 * `issues` is passed in rather than read off the hives because an open
 * issue is a different fact from an overdue inspection: a hive can be
 * perfectly on schedule and still have something wrong with it, and that
 * is the case the red state exists for.
 */
export function summariseSite(siteId, siteName, hives, issuesByHive) {
  // Boolean, not a numeric comparison: the map holds "urgent"/"watch",
  // and "urgent" > 0 is false, which silently hid every issue.
  const withIssues = hives.filter((h) => Boolean(issuesByHive[h.hive_id]));
  const urgentIssues = hives.filter((h) => issuesByHive[h.hive_id] === "urgent");
  const neverChecked = hives.filter((h) => !h.last_checked);
  const overdue = hives.filter((h) => h.needs_attention && h.last_checked);
  const soon = hives.filter((h) => {
    if (h.needs_attention || !h.last_checked) return false;
    const days = daysUntil(h.next_check_due);
    return days !== null && days <= SOON_DAYS;
  });

  // First match wins, worst first. Never-checked counts as overdue rather
  // than as its own state: at site level the useful distinction is "needs
  // a visit" versus "does not", and the hive rows below say which kind.
  let state = "current";
  if (withIssues.length > 0) state = "issues";
  else if (overdue.length > 0 || neverChecked.length > 0) state = "overdue";
  else if (soon.length > 0) state = "soon";

  return {
    site_id: siteId,
    site_name: siteName,
    hive_count: hives.length,
    state,
    issue_count: withIssues.length,
    has_urgent_issue: urgentIssues.length > 0,
    overdue_count: overdue.length + neverChecked.length,
    soon_count: soon.length,
    // What the card says under the name. One line, and it must be true
    // even when several things are wrong at once -- so it leads with the
    // worst and mentions the rest only if there is room for it to matter.
    headline: buildHeadline({
      issues: withIssues.length,
      overdue: overdue.length + neverChecked.length,
      soon: soon.length,
      nextDays: soonestCheck(hives),
    }),
    // The single hive a keeper should look at first if they open this
    // site. Same ranking the "needs you now" list uses.
    worst_hive: rankHives(hives, issuesByHive)[0] || null,
  };
}

/** The nearest scheduled check across a yard, in days. */
function soonestCheck(hives) {
  const days = (hives || [])
    .map((h) => daysUntil(h.next_check_due))
    .filter((d) => d !== null);
  return days.length ? Math.min(...days) : null;
}

function buildHeadline({ issues, overdue, soon, nextDays }) {
  const parts = [];
  if (issues) parts.push(`${issues} with issues`);
  if (overdue) parts.push(`${overdue} due`);
  if (soon && parts.length === 0) parts.push(`${soon} due soon`);
  if (parts.length) return parts.join(" \u00b7 ");

  // Nothing is wrong, and the badge above already says so -- repeating
  // "Up to date" here would spend the only line on the card saying the
  // same thing twice. When the next visit is due is the useful answer.
  if (nextDays === null) return "No checks scheduled";
  if (nextDays <= 0) return "Next check due today";
  return `Next check in ${nextDays} day${nextDays === 1 ? "" : "s"}`;
}

/**
 * Hives ordered by what needs a person, across any number of sites.
 *
 * Extends hiveUrgency's rank with the one thing it cannot see: an open
 * issue. A hive inspected yesterday with an urgent issue outranks one
 * that is merely overdue, because the schedule is a proxy for trouble and
 * an issue is trouble itself.
 */
/** Below this, a health score is worth interrupting someone about. */
export const POOR_HEALTH = 60;

export function rankHives(hives, issuesByHive = {}, healthByHive = {}) {
  const score = (h) => {
    const issue = issuesByHive[h.hive_id];
    if (issue === "urgent") return 100;
    if (issue) return 80;

    // A failing health score on a hive that is otherwise ON SCHEDULE is
    // the one thing the schedule cannot tell you -- it is why the Health
    // Triage card existed, and folding it in here is what makes that card
    // redundant rather than merely duplicated. Ranked between an open
    // issue and a missed inspection: a low score is evidence something is
    // wrong, where an overdue check is only the absence of evidence.
    const health = healthByHive[h.hive_id];
    if (typeof health === "number" && health < POOR_HEALTH) return 70 - health / 10;

    // 2 = never checked, 1 = overdue, 0 = fine.
    return urgencyRank(h) * 20;
  };
  return [...(hives || [])].sort((a, b) => {
    const diff = score(b) - score(a);
    if (diff !== 0) return diff;
    // Tie-break on how overdue, so the longest-neglected surfaces first
    // rather than whichever the database happened to return first.
    const da = daysUntil(a.next_check_due);
    const db = daysUntil(b.next_check_due);
    if (da === null) return -1;
    if (db === null) return 1;
    return da - db;
  });
}

/** {hive_id: "urgent" | "watch"} from the open-issues list. */
export function issuesByHive(openIssues) {
  const map = {};
  for (const issue of openIssues || []) {
    const level = issue.severity === "Urgent" ? "urgent" : "watch";
    // Urgent wins if a hive has both.
    if (map[issue.hive_id] !== "urgent") map[issue.hive_id] = level;
  }
  return map;
}

/** Every site the hives belong to, worst first. */
export function rollUpSites(hives, openIssues) {
  const byHive = issuesByHive(openIssues);
  const groups = new Map();
  for (const hive of hives || []) {
    const key = hive.site_id ?? `unsited-${hive.site_name || ""}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(hive);
  }

  const sites = [...groups.entries()].map(([key, group]) =>
    summariseSite(
      typeof key === "number" ? key : null,
      group[0]?.site_name || "Unassigned",
      group,
      byHive,
    ),
  );

  return sites.sort((a, b) => {
    const diff = SITE_STATES.indexOf(a.state) - SITE_STATES.indexOf(b.state);
    if (diff !== 0) return diff;
    // Then by how much is wrong, so a yard with six overdue hives sits
    // above one with a single overdue hive.
    return (b.issue_count + b.overdue_count) - (a.issue_count + a.overdue_count);
  });
}
